import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import SectionHeader from '@/components/base/SectionHeader';
import ActionButtons from '@/components/feature/ActionButtons';
import { getMoviesByIds, getNewsByStar, getStarById, formatDate } from '@/utils/media';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function StarDetail() {
  const { id = '' } = useParams();
  const star = getStarById(id);

  usePageMeta({
    title: star ? `${star.name} - 河北彩花明星资料` : '未找到资料 - 河北彩花影视大全',
    description: star
      ? `${star.name}明星资料：${star.profession}，${star.constellation}，${star.birthYear}年出生。${star.bio}`
      : '抱歉，未找到对应的明星资料。',
    keywords: star ? `${star.name},${star.tags.join(',')},河北彩花明星资料` : '河北彩花明星资料',
  });

  if (!star) {
    return (
      <div className="mx-auto flex w-full max-w-7xl flex-col items-center px-4 py-24 text-center md:px-6">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-error-warning-line text-3xl"></i>
        </div>
        <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">未找到该明星资料</h1>
        <p className="mt-2 text-sm text-foreground-500">该资料可能不存在，或链接已失效。</p>
        <Link
          to="/stars"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line"></i>
          返回明星列表
        </Link>
      </div>
    );
  }

  const works = getMoviesByIds(star.representativeWorks);
  const relatedNews = getNewsByStar(star.id);

  const infoRows = [
    { label: '出生日期', value: star.birthday },
    { label: '星座', value: star.constellation },
    { label: '出生年份', value: `${star.birthYear}年` },
    { label: '国籍', value: star.nationality },
    { label: '职业', value: star.profession },
    { label: '身高', value: star.height },
    { label: '出道时间', value: star.debut },
  ];

  return (
    <div className="w-full">
      {/* 主视觉 */}
      <div className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-12">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '明星资料', to: '/stars' }, { label: star.name }]} />
          <div className="mt-6 flex flex-col gap-7 md:flex-row md:items-start">
            <div className="h-[240px] w-[240px] shrink-0 overflow-hidden rounded-lg bg-background-200">
              <img
                src={star.avatar}
                alt={`${star.name} 明星资料头像`}
                title={`${star.name} 河北彩花明星资料`}
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap gap-2">
                <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
                  {star.profession}
                </span>
                <span className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900">
                  {star.nationality}
                </span>
              </div>
              <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">{star.name}</h1>
              <p className="mt-2 text-sm text-foreground-500">
                {star.constellation} · {star.birthYear}年出生 · 身高 {star.height} · {star.debut}出道
              </p>
              <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-700">{star.bio}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {star.tags.map((tag) => (
                  <span key={tag} className="rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-700">
                    {tag}
                  </span>
                ))}
              </div>
              <div className="mt-6">
                <ActionButtons id={`star-${star.id}`} title={star.name} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            {/* 个人简介 */}
            <SectionHeader icon="ri-user-3-line" title="个人简介" subtitle="基本资料与发展概述" />
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <p className="text-sm leading-loose text-foreground-700">{star.bio}</p>
              <p className="mt-4 text-sm leading-loose text-foreground-700">
                以上资料为示范演示内容，代表作品与个人经历均为示例数据，用于展示信息结构，不代表真实公开信息。
              </p>
            </div>

            {/* 发展历程 */}
            <div className="mt-10">
              <SectionHeader icon="ri-history-line" title="发展历程" subtitle="重要节点回顾" />
              <ol className="relative flex flex-col gap-6 border-l border-background-300 pl-6">
                {star.timeline.map((item) => (
                  <li key={item.year} className="relative">
                    <span className="absolute -left-[31px] top-1 flex h-3 w-3 items-center justify-center rounded-full bg-accent-500"></span>
                    <p className="font-heading text-sm font-bold text-primary-600">{item.year}</p>
                    <p className="mt-1 text-sm leading-relaxed text-foreground-700">{item.event}</p>
                  </li>
                ))}
              </ol>
            </div>

            {/* 相关新闻 */}
            {relatedNews.length > 0 && (
              <div className="mt-10">
                <SectionHeader icon="ri-newspaper-line" title="相关资讯" subtitle="与该明星相关的动态" />
                <ul className="flex flex-col divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
                  {relatedNews.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={`/news?open=${item.id}`}
                        className="group flex items-start gap-4 p-4 transition-colors hover:bg-background-100"
                      >
                        <span className="mt-0.5 shrink-0 rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-medium text-primary-700">
                          {item.category}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                            {item.title}
                          </span>
                          <span className="mt-1 block text-xs text-foreground-400">{formatDate(item.date)}</span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <aside className="flex flex-col gap-6">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h3 className="font-heading text-base font-bold text-foreground-950">基本信息</h3>
              <dl className="mt-4 flex flex-col gap-3 text-sm">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex justify-between gap-3">
                    <dt className="text-foreground-500">{row.label}</dt>
                    <dd className="text-right font-medium text-foreground-900">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </aside>
        </div>

        {/* 代表作品 */}
        {works.length > 0 && (
          <div className="mt-14">
            <SectionHeader icon="ri-film-line" title="代表作品" subtitle="点击查看作品详情" />
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {works.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}